import React from "react";

/**
 * Shared title bar for dialogs and modals — an accent-tinted band that follows
 * the tenant theme (see theme.css), factored out so every modal looks
 * consistent.
 *
 * Props:
 *   title     string | node  — the heading text
 *   onClose   () => void      — optional; renders a × button when provided
 *   variant   "accent" | "danger" — danger uses the fixed red tint for
 *                               destructive confirmations
 *   color     string          — legacy CSS background override; prefer variant
 *   disabled  bool            — disable the close button
 *
 * Pair with a full-bleed content container: for Radix Dialog.Content set
 * `style={{ padding: 0 }}` and wrap the body in a padded div; the CSS classes
 * (.modal-titlebar / .modal-body) live in carstyles.css.
 */
/**
 * The band itself, without any opinion about how closing works.
 *
 * Two components render this markup: this file's default export (imperative
 * onClose) and RadixModalHeader (Radix's declarative Dialog.Close plus the
 * hidden Dialog.Title that Radix requires for accessibility). Those close
 * mechanisms are genuinely different and should stay separate — what should not
 * be duplicated is the band, which is why the twin's own docstring admitted it
 * was "sharing the look of ModalTitleBar" and then diverged: it never grew
 * `disabled` or `className`, so migrating a modal to Radix silently dropped its
 * disabled-close guard.
 *
 * `children` is the close-control slot. Pass nothing for a bar with no ×.
 *
 * `icon` is a component reference (a lucide icon, e.g. `icon={Car}`), not an
 * element — the shell sizes it so every header glyph is identical. It is drawn
 * in currentColor rather than a filled badge: a badge suits one alert dialog but
 * is heavy repeated across thirty form modals, and currentColor means the danger
 * and success variants recolor the glyph for free.
 *
 * `subtitle` is an optional second line under the title, for the message modals
 * that need to name what they are acting on.
 */
export function ModalTitleBarShell({ title, subtitle, icon: Icon, variant = "accent", color, className = "", children }) {
  const classes = ["modal-titlebar"];
  if (variant === "danger") classes.push("is-danger");
  if (variant === "success") classes.push("is-success");
  if (className) classes.push(className);
  return (
    <div className={classes.join(" ")} style={color ? { background: color } : undefined}>
      {/* minWidth:0 so a long title still truncates instead of shoving the × out. */}
      <div className="modal-titlebar-lead">
        {Icon && <Icon className="modal-titlebar-icon" size={18} aria-hidden="true" />}
        <div style={{ minWidth: 0 }}>
          {/* Plain h2, not Radix Heading: its leading-trim throws off vertical centering. */}
          <h2 className="modal-titlebar-title">{title}</h2>
          {subtitle && <p className="modal-titlebar-subtitle">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

export default function ModalTitleBar({ title, subtitle, icon, onClose, variant = "accent", color, disabled = false, className = "" }) {
  return (
    <ModalTitleBarShell title={title} subtitle={subtitle} icon={icon} variant={variant} color={color} className={className}>
      {onClose && (
        <button
          type="button"
          className="modal-titlebar-close"
          aria-label="Close"
          onClick={onClose}
          disabled={disabled}
        >
          ×
        </button>
      )}
    </ModalTitleBarShell>
  );
}
